/**
 * Surface rectangle-footprint primitives — Object Mode tools for placing
 * boxes and cylinders on any surface.
 *
 * Three-stage modal interaction:
 *
 *   1. Stage `select`: click a face to lock an oriented surface grid. Shift
 *      click up to two joining faces on the same object to average the grid
 *      normal across an edge or corner.
 *
 *   2. Stage `footprint`: the next drag places the second rectangle corner
 *      on that locked plane, with snap (vertex / edge / face / surface /
 *      grid) projected back onto the plane. Shift constrains to a square
 *      anchored at A; Alt/Option treats A as the footprint center. Surface
 *      Cylinder reads that same square as a circle footprint.
 *
 *      Camera orbit is suppressed from surface selection through footprint
 *      creation. As soon as the silhouette is released the active flag is
 *      cleared, the camera unlocks, and the user is free to orbit before
 *      extruding.
 *
 *   3. Stage `height`: pointer ownership transfers to
 *      `SurfaceFootprintGizmo` (the unified Box+Cylinder gizmo). Pulling
 *      the silhouette into a 3D primitive requires grabbing the gizmo
 *      arrow — the same "must grab the gizmo" pattern used by
 *      `OperatorGizmo` for extrude / bevel / inset in Edit Mode. This
 *      handler does not touch pointer events in the height stage; it only
 *      owns the cancel paths (Escape / blur / pointercancel) and the
 *      external-disarm subscribe.
 *
 * Confirm builds a brand-new generator-backed `SceneObject` and pushes one
 * undo entry. Cancel paths (Esc, blur, pointercancel, sub-epsilon footprint,
 * sub-epsilon height) leave the scene untouched.
 */

import { useThree } from "@react-three/fiber";
import { useEffect } from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../app/store.ts";
import type {
  SurfaceFootprintDraftState,
  SurfaceFootprintStage,
  SurfaceToolState,
} from "../app/storeTypes.ts";
import { selectSurfaceToolOfKind } from "../app/surfaceToolSelectors.ts";
import { intersectRayWithBasisPlane } from "../modeling/basisPlaneMath.ts";
import type { FaceBasis } from "../modeling/faceBasis.ts";
import {
  projectPointOntoPlane,
  querySnap,
  setLastSnapResult,
} from "../modeling/snap.ts";
import {
  cubeTransformFromDraft,
  resolveSurfaceFootprintCorners,
} from "../modeling/surfaceBoxMath.ts";
import {
  DRAW_DRAG_THRESHOLD_PX,
  SURFACE_CONFIRM_EPSILON,
} from "../modeling/surfaceConstants.ts";
import type { ViewportSurfaceHit } from "../modeling/viewportSurfaceRaycast.ts";
import { createRafCoalescer } from "../rafCoalescer.ts";
import {
  dynamicSurfaceGridSpacing,
  ndcFromClient,
  pointerShiftActive,
  snapPointToSurfaceGrid,
} from "./surfaceModalUtils.ts";
import {
  clearSurfaceSelectionHighlight,
  setSurfaceSelectionHighlightHover,
  setSurfaceSelectionHighlightSelection,
} from "./surfaceSelectionHighlight.ts";
import {
  resolveJoinedSurfaceSelectionAtPointer,
  resolveSurfaceSelectionAtPointer,
} from "./surfaceSelectionInput.ts";

const EMPTY_EXCLUDE = new Set<string>();

type FootprintKind = "box" | "cylinder";
type FootprintToolState = Extract<SurfaceToolState, { kind: FootprintKind }>;

interface SurfaceFootprintToolConfig {
  kind: FootprintKind;
  operatorId: "surface_box" | "surface_cylinder";
}

const SURFACE_BOX_TOOL: SurfaceFootprintToolConfig = {
  kind: "box",
  operatorId: "surface_box",
};

const SURFACE_CYLINDER_TOOL: SurfaceFootprintToolConfig = {
  kind: "cylinder",
  operatorId: "surface_cylinder",
};

export function SurfaceBoxHandler() {
  return <SurfaceFootprintHandler tool={SURFACE_BOX_TOOL} />;
}

export function SurfaceCylinderHandler() {
  return <SurfaceFootprintHandler tool={SURFACE_CYLINDER_TOOL} />;
}

function SurfaceFootprintHandler({
  tool,
}: {
  tool: SurfaceFootprintToolConfig;
}) {
  const { camera, gl } = useThree();

  useEffect(() => {
    const canvas = gl.domElement;

    // Local refs — pointer state lives outside the store to avoid React
    // re-renders during a 60Hz drag.
    let pointerId = -1;
    let stage: SurfaceFootprintStage | null = null;
    let basis: FaceBasis | null = null;
    let hostObjectId: string | null = null;
    let primarySurfaceHit: ViewportSurfaceHit | null = null;
    let selectedSurfaceHits: ViewportSurfaceHit[] = [];
    let cornerA: [number, number, number] = [0, 0, 0];
    let lastClientX = 0;
    let lastClientY = 0;
    let shiftHeld = false;
    let altHeld = false;
    let pendingFootprint: {
      point: [number, number, number] | null;
      clientX: number;
      clientY: number;
    } | null = null;
    let prevCursor = "";

    // Tool-state helpers. The polymorphic `surfaceTool` slot is the single
    // source of truth; these locals narrow it down for the footprint
    // discriminant and hide the ceremony from the event handlers.
    function getToolState(): FootprintToolState | null {
      return selectSurfaceToolOfKind(useStore.getState(), tool.kind);
    }
    function patchToolState(patch: Partial<FootprintToolState>): void {
      const cur = getToolState();
      if (!cur) return;
      useStore.getState().setSurfaceTool({ ...cur, ...patch });
    }
    function setToolState(next: FootprintToolState): void {
      useStore.getState().setSurfaceTool(next);
    }

    // rAF-coalesced pointer dispatch — mirrors `OperatorGizmo` / sketch /
    // brush handlers. Only the footprint stage runs through the canvas
    // pointer event loop; the height stage is owned by
    // `SurfaceFootprintGizmo` (see this file's module docstring).
    const coalescer = createRafCoalescer<{ x: number; y: number }>(
      ({ x, y }) => {
        lastClientX = x;
        lastClientY = y;
        if (stage === "footprint") updateFootprint();
      },
    );

    // Reusable scratch — avoids per-frame allocation in the rAF loop.
    const scratchA = new THREE.Vector3();
    const scratchB = new THREE.Vector3();
    const scratchC = new THREE.Vector3();
    const scratchOrigin = new THREE.Vector3();
    const scratchDirection = new THREE.Vector3();

    function isArmed(): boolean {
      const s = useStore.getState();
      return (
        s.activeOperator === tool.operatorId && s.workspaceMode === "object"
      );
    }

    function reset(restoreCursor = true) {
      coalescer.cancel();
      if (pointerId !== -1) {
        try {
          canvas.releasePointerCapture(pointerId);
        } catch {
          // capture may already be lost (pointercancel, blur)
        }
        pointerId = -1;
      }
      if (restoreCursor && stage !== null) {
        canvas.style.cursor = prevCursor;
      }
      stage = null;
      basis = null;
      hostObjectId = null;
      primarySurfaceHit = null;
      selectedSurfaceHits = [];
      pendingFootprint = null;
      shiftHeld = false;
      altHeld = false;
      setLastSnapResult(null);
      clearSurfaceSelectionHighlight();
      // Clear the slot entirely rather than zeroing fields in place.
      // `publishDraft` re-initialises with stage="select" only when the
      // slot is null; leaving an empty `{ kind, stage: null, ... }` shell
      // here would cause the next click to keep stage=null and hide the
      // alignment grid until the footprint drag started. (Repro: blur the
      // browser mid-modal, refocus, click a face — without this the grid
      // would not appear until the drag actually began.)
      if (getToolState()) {
        useStore.getState().setSurfaceTool(null);
      }
    }

    function publishDraft(
      draftCornerA: [number, number, number],
      draftCornerB: [number, number, number],
    ) {
      if (!basis) return;
      // The handler only touches the draft during the footprint stage;
      // height stays zero until the gizmo takes over.
      const draft: SurfaceFootprintDraftState = {
        basis,
        hostObjectId,
        cornerA: draftCornerA,
        cornerB: draftCornerB,
        height: 0,
        profileTarget: { kind: "face" },
      };
      // First write of the modal initialises the slot; subsequent writes
      // patch the draft. `selectSurfaceAtPointer` is the one entry point
      // that may run while `surfaceTool` is still null (the operator was
      // just armed, no face picked yet) — fall through to setToolState in
      // that case.
      const cur = getToolState();
      if (cur) {
        useStore.getState().setSurfaceTool({ ...cur, draft });
      } else {
        setToolState({
          kind: tool.kind,
          stage: "select",
          draft,
          active: true,
        });
      }
    }

    function selectSurfaceAtPointer(e: PointerEvent): boolean {
      const result = resolveSurfaceSelectionAtPointer(
        canvas,
        e.clientX,
        e.clientY,
      );
      if (!result) return false;
      basis = result.basis;
      hostObjectId = result.hostObjectId;
      primarySurfaceHit = result.hit;
      selectedSurfaceHits = [result.hit];
      setSurfaceSelectionHighlightSelection(selectedSurfaceHits);
      setSurfaceSelectionHighlightHover(null);
      cornerA = result.basis.origin;
      publishDraft(cornerA, cornerA);
      return true;
    }

    function joinSurfaceAtPointer(e: PointerEvent): boolean {
      if (!primarySurfaceHit || selectedSurfaceHits.length >= 3) return false;
      const result = resolveJoinedSurfaceSelectionAtPointer(
        canvas,
        e.clientX,
        e.clientY,
        selectedSurfaceHits,
      );
      if (!result) return false;
      basis = result.basis;
      hostObjectId = result.hostObjectId;
      selectedSurfaceHits = [...selectedSurfaceHits, result.hit];
      setSurfaceSelectionHighlightSelection(selectedSurfaceHits);
      setSurfaceSelectionHighlightHover(null);
      cornerA = result.basis.origin;
      publishDraft(cornerA, cornerA);
      return true;
    }

    function updateSurfaceHoverAtPointer(e: PointerEvent) {
      if (!isArmed()) return;
      if (stage !== null && stage !== "select") {
        setSurfaceSelectionHighlightHover(null);
        return;
      }
      const result = resolveSurfaceSelectionAtPointer(
        canvas,
        e.clientX,
        e.clientY,
      );
      setSurfaceSelectionHighlightHover(result?.hit ?? null);
    }

    function pointOnSelectedSurface(
      e: PointerEvent,
    ): [number, number, number] | null {
      if (!basis) return null;
      const rect = canvas.getBoundingClientRect();
      const { ndcX, ndcY } = ndcFromClient(e.clientX, e.clientY, rect);
      const state = useStore.getState();

      if (state.snapEnabled) {
        const snap = querySnap(ndcX, ndcY, state.snapTarget, EMPTY_EXCLUDE, 0);
        setLastSnapResult(snap);
        if (snap) {
          const projected = projectPointOntoPlane(
            snap.worldPosition,
            basis.origin,
            basis.normal,
            scratchA,
          );
          return [projected.x, projected.y, projected.z];
        }
        if (state.snapTarget === "grid") {
          const hit = intersectRayWithBasisPlane(
            ndcX,
            ndcY,
            camera,
            basis,
            scratchB,
            scratchOrigin,
            scratchDirection,
          );
          if (hit) {
            return snapPointToSurfaceGrid(
              basis,
              [hit.x, hit.y, hit.z],
              dynamicSurfaceGridSpacing(state.gridLogDist),
            );
          }
        }
      }

      const hit = intersectRayWithBasisPlane(
        ndcX,
        ndcY,
        camera,
        basis,
        scratchC,
        scratchOrigin,
        scratchDirection,
      );
      return hit ? [hit.x, hit.y, hit.z] : null;
    }

    function updateFootprint() {
      if (!basis) return;
      const rect = canvas.getBoundingClientRect();
      const { ndcX, ndcY } = ndcFromClient(lastClientX, lastClientY, rect);
      const state = useStore.getState();

      // 1. Snap path — vertex/edge/face/surface return a world target that may
      //    be off-plane; project back onto the locked plane so the footprint
      //    stays planar.
      let cornerB: [number, number, number] | null = null;

      if (state.snapEnabled) {
        const snap = querySnap(ndcX, ndcY, state.snapTarget, EMPTY_EXCLUDE, 0);
        setLastSnapResult(snap);
        if (snap) {
          const projected = projectPointOntoPlane(
            snap.worldPosition,
            basis.origin,
            basis.normal,
            scratchA,
          );
          cornerB = [projected.x, projected.y, projected.z];
        } else if (state.snapTarget === "grid") {
          // Pure grid snap — intersect the pointer ray with the basis plane,
          // round (u, v) to the dynamic spacing, reconstruct world point.
          const hit = intersectRayWithBasisPlane(
            ndcX,
            ndcY,
            camera,
            basis,
            scratchB,
            scratchOrigin,
            scratchDirection,
          );
          if (hit) {
            cornerB = snapPointToSurfaceGrid(
              basis,
              [hit.x, hit.y, hit.z],
              dynamicSurfaceGridSpacing(state.gridLogDist),
            );
          }
        }
      }

      // 2. No snap (or snap missed and target wasn't grid) — plain plane hit.
      if (!cornerB) {
        const hit = intersectRayWithBasisPlane(
          ndcX,
          ndcY,
          camera,
          basis,
          scratchC,
          scratchOrigin,
          scratchDirection,
        );
        if (hit) {
          cornerB = [hit.x, hit.y, hit.z];
        }
      }

      if (!cornerB) return;

      // 3. Modifier constraints — done in basis (u, v). Shift makes the
      //    footprint square; Alt/Option keeps the first hit at the center.
      const corners = resolveSurfaceFootprintCorners(basis, cornerA, cornerB, {
        centered: altHeld,
        square: shiftHeld,
      });

      publishDraft(corners.cornerA, corners.cornerB);
    }

    function onPointerDown(e: PointerEvent) {
      if (e.button !== 0) return;
      if (!isArmed()) return;
      if (pointerId !== -1) return; // already in a drag
      // The height stage is owned by the gizmo. The canvas stays free for
      // orbit so the user can reframe before grabbing the arrow.
      if (stage === "height" || stage === "footprint") return;

      if (stage === null) {
        // selectSurfaceAtPointer initialises the surfaceTool slot through
        // publishDraft (stage "select", active true) — no follow-up patch
        // is needed.
        if (!selectSurfaceAtPointer(e)) return;
        e.preventDefault();
        e.stopPropagation();
        prevCursor = canvas.style.cursor;
        canvas.style.cursor = "crosshair";
        stage = "select";
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      const startPoint = pointOnSelectedSurface(e);
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        // capture may fail under some PointerEvents shims — proceed anyway.
      }
      pointerId = e.pointerId;
      pendingFootprint = {
        point: startPoint
          ? [startPoint[0], startPoint[1], startPoint[2]]
          : null,
        clientX: e.clientX,
        clientY: e.clientY,
      };
      shiftHeld = pointerShiftActive(e);
      altHeld = e.altKey;
      lastClientX = e.clientX;
      lastClientY = e.clientY;
      canvas.style.cursor = "crosshair";
    }

    function onPointerMove(e: PointerEvent) {
      if (pointerId === -1) {
        updateSurfaceHoverAtPointer(e);
        return;
      }
      if (e.pointerId !== pointerId) return;
      shiftHeld = pointerShiftActive(e);
      altHeld = e.altKey;
      if (stage === "select" && pendingFootprint) {
        const dx = e.clientX - pendingFootprint.clientX;
        const dy = e.clientY - pendingFootprint.clientY;
        if (Math.hypot(dx, dy) < DRAW_DRAG_THRESHOLD_PX) return;
        if (!pendingFootprint.point) return;
        cornerA = pendingFootprint.point;
        pendingFootprint = null;
        stage = "footprint";
        setSurfaceSelectionHighlightHover(null);
        patchToolState({ active: true, stage: "footprint" });
        publishDraft(cornerA, cornerA);
      }
      coalescer.schedule({ x: e.clientX, y: e.clientY });
    }

    function onPointerUp(e: PointerEvent) {
      if (e.button !== 0) return;
      if (e.pointerId !== pointerId) return;
      if (stage === "select" && pendingFootprint) {
        const joinClick = pointerShiftActive(e);
        try {
          canvas.releasePointerCapture(pointerId);
        } catch {
          // ignore
        }
        pointerId = -1;
        pendingFootprint = null;
        setLastSnapResult(null);
        shiftHeld = false;
        if (joinClick) {
          joinSurfaceAtPointer(e);
        } else {
          selectSurfaceAtPointer(e);
        }
        return;
      }
      // Only the footprint stage runs through canvas pointer events; the
      // height stage's confirm path lives on the gizmo.
      if (stage !== "footprint") return;

      coalescer.flush();
      shiftHeld = pointerShiftActive(e);
      altHeld = e.altKey;

      const draft = getToolState()?.draft ?? null;
      if (!draft || !basis) {
        reset();
        return;
      }
      const t = cubeTransformFromDraft(draft);
      if (
        Math.abs(t.width) < SURFACE_CONFIRM_EPSILON ||
        Math.abs(t.depth) < SURFACE_CONFIRM_EPSILON
      ) {
        // Misclick — no scene change.
        reset();
        return;
      }
      // Promote to height stage. Release pointer capture, restore the
      // cursor, and clear the active flag — the gizmo now owns extrusion
      // and the camera unlocks so the user can orbit before grabbing it.
      try {
        canvas.releasePointerCapture(pointerId);
      } catch {
        // ignore
      }
      pointerId = -1;
      pendingFootprint = null;
      stage = "height";
      shiftHeld = false;
      altHeld = false;
      canvas.style.cursor = prevCursor;
      patchToolState({ stage: "height", active: false });
    }

    function onPointerCancel(e: PointerEvent) {
      if (e.pointerId !== pointerId && pointerId !== -1) return;
      reset();
    }

    function onPointerLeave() {
      setSurfaceSelectionHighlightHover(null);
    }

    function onWindowBlur() {
      reset();
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (stage === null && !isArmed()) return;
        e.preventDefault();
        if (stage === null) {
          useStore.getState().armToolOperator(null);
          return;
        }
        reset();
        return;
      }
      if (stage === null) return;
      if (e.key === "Shift" && !shiftHeld) {
        shiftHeld = true;
        if (stage === "footprint") {
          coalescer.schedule({ x: lastClientX, y: lastClientY });
        }
      }
      if (e.key === "Alt" && !altHeld) {
        altHeld = true;
        if (stage === "footprint") {
          coalescer.schedule({ x: lastClientX, y: lastClientY });
        }
      }
    }

    function onKeyUp(e: KeyboardEvent) {
      if (e.key === "Shift" && shiftHeld) {
        shiftHeld = false;
        if (stage === "footprint") {
          coalescer.schedule({ x: lastClientX, y: lastClientY });
        }
      }
      if (e.key === "Alt" && altHeld) {
        altHeld = false;
        if (stage === "footprint") {
          coalescer.schedule({ x: lastClientX, y: lastClientY });
        }
      }
    }

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointercancel", onPointerCancel);
    canvas.addEventListener("pointerleave", onPointerLeave);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onWindowBlur);

    // External disarm: the palette button (or any other caller) can clear
    // `activeOperator` while the handler is mid-modal. The store's
    // armToolOperator already nulls stage/draft/active, but the handler also
    // owns cursor + pointer-capture state — without this listener the
    // crosshair cursor sticks after the tool is toggled off mid-drag.
    const unsubscribe = useStore.subscribe((s, prev) => {
      if (
        prev.activeOperator === tool.operatorId &&
        s.activeOperator !== tool.operatorId
      ) {
        reset();
      }
    });

    return () => {
      reset();
      unsubscribe();
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerCancel);
      canvas.removeEventListener("pointerleave", onPointerLeave);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onWindowBlur);
    };
  }, [camera, gl, tool]);

  return null;
}
